"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BqVisitor = void 0;
const sheet_db_1 = require("../../../sheet_db");
const string_utils_1 = require("../../../utils/string.utils");
const antlr_files_1 = require("../../antlr_files");
const types_1 = require("../../types");
const array_node_1 = require("../array.node");
const base_1 = require("../base");
const concrete_node_1 = require("../concrete.node");
const constant_node_1 = require("../constant.node");
const simple_formula_node_1 = require("../simple.formula.node");
const visitor_base_1 = require("./visitor.base");
const utils_1 = require("../../../utils");
class BqVisitor extends visitor_base_1.VisitorBase {
    ast;
    plan;
    formulaPrefix;
    tableAlias;
    constructor(ast, env, dataset, plan, tableAlias) {
        super();
        this.ast = ast;
        this.plan = plan;
        this.formulaPrefix = `\`${env}\`.\`${dataset}\`.`;
        this.tableAlias = tableAlias ? `\`${tableAlias}\`.` : '';
    }
    visitComparisonExpr(node) {
        this.replaceExprBySimpleFormula(node);
    }
    visitBooleanExpr(node) {
        this.replaceExprBySimpleFormula(node);
    }
    visitParenthesisExpr(node) {
        // clean the concrete nodes (remove the parenthesis)
        node.children = node.children;
    }
    visitPlusMinusExpr(node) {
        this.replaceExprBySimpleFormula(node);
    }
    visitTimesDivExpr(node) {
        this.replaceExprBySimpleFormula(node);
    }
    visitPowExpr(node) {
        this.replaceExprBySimpleFormula(node);
    }
    visitSignedExpr(node) {
        let children = '';
        for (const child of node.children) {
            child.parent = undefined;
            children = children.concat(child.toString() ?? '');
        }
        children = this.addQuotationMarks(children).replaceAll(' ', '');
        node.children = [constant_node_1.ConstantNode.createConstantNode(children, node)];
    }
    visitColumn(node) {
        if (node.isPlanVariable() && this.plan && (0, types_1.isPlanAttributes)(this.plan)) {
            const planVariable = node.getPlanVariable(this.plan);
            // format isodate
            const isoDatePlanVariable = new Date(planVariable).toISOString();
            const formattedVariable = this.getFormattedValue(isoDatePlanVariable);
            node.text = this.addQuotationMarks(formattedVariable);
            return;
        }
        const column_id = node.text.replaceAll('__', '');
        if (node.queryReplacer) {
            node.text = node.queryReplacer(column_id);
            return;
        }
        node.text = `${this.tableAlias}\`_${column_id}\``;
    }
    getFormattedValue(value) {
        const v2Formatter = (date) => {
            const parsedDate = (0, utils_1.validateAndParseDate)(date, 'yyyy-MM-dd');
            return parsedDate.parsedString;
        };
        const v1Formatter = (date) => date;
        const formatter = this.plan?.comparisonFormulaMode === 'strict_type_formulas' ? v2Formatter : v1Formatter;
        return formatter(value);
    }
    normalizeQuestionMark = (text) => {
        return (0, string_utils_1.normalizeEscapes)(text, '?', '\\\\?');
    };
    visitConstant(node) {
        if (node.terminalType === antlr_files_1.Parser.STRING)
            node.text = this.addQuotationMarks((0, string_utils_1.sanitizeString)(node.text.slice(1, node.text.length - 1)));
        node.text = this.normalizeQuestionMark(node.text);
        if (node.parent?.treeNodeType === base_1.TreeNodeType.SIGNED_NODE) {
            return;
        }
        if (node.isRegexPattern()) {
            node.text = `r${node.text}`;
        }
        if (antlr_files_1.Parser.NUMBER_TYPES.has(node.terminalType)) {
            node.text = this.addQuotationMarks(node.text);
        }
        if (antlr_files_1.Parser.BOOL === node.terminalType) {
            const text = this.plan?.comparisonFormulaMode === 'strict_type_formulas'
                ? node.text.toUpperCase()
                : node.text;
            node.text = this.addQuotationMarks(text);
        }
    }
    visitFormula(node) {
        node.formulaName = `${this.formulaPrefix}${node.formulaName}`;
        this.convertVariadicToArray(node);
        this.replaceOptionalArgsByNull(node);
    }
    replaceExprBySimpleFormula(node) {
        const newNode = new simple_formula_node_1.SimpleFormulaNode(node, node.parent ?? undefined, this.formulaPrefix, this.plan);
        if (!node.parent) {
            this.ast._root = newNode;
        }
    }
    /**
     * Iterate through the children formulas and replace optional arguments by NULL.
     *
     * @example
     * // formula -> FIND('random string', __8__)
     *
     * replaceOptionalArgsByNull(formula)
     *
     * // result -> FIND('random string', \`_8\`, NULL, NULL)
     *
     * @param node - The formula node to add concrete nodes.
     */
    replaceOptionalArgsByNull(node) {
        const formulaDef = sheet_db_1.FormulaLibrary[node.abstractText];
        if (!formulaDef.args_def)
            return;
        const children = [...node.children];
        for (const [index, argDef] of formulaDef.args_def.entries()) {
            if (!argDef.required && !children[index]) {
                children.push(constant_node_1.ConstantNode.createConstantNode('NULL', node));
            }
        }
        node.children = children;
        this.addConcreteNodesToFormula(node);
    }
    /**
     * Adds concrete nodes to the formula node by creating concrete nodes for separators and parentheses because the FormulaNode children getter removes all concrete nodes from the formula.
     *
     * @see {@link splitscript/custom_tree/formula.node.ts:133}
     *
     * @param node - The formula node to add concrete nodes.
     */
    addConcreteNodesToFormula(node) {
        let children = [...node.children];
        children = children
            .map((child, i) => {
            if (i === 0)
                return [child];
            return [concrete_node_1.ConcreteNode.createConcreteNode(', ', node), child];
        })
            .flat();
        children.unshift(concrete_node_1.ConcreteNode.createConcreteNode('(', node));
        children.push(concrete_node_1.ConcreteNode.createConcreteNode(')', node));
        node.children = children;
    }
    /**
     * Converts variadic arguments in the formula node to an array representation.
     *
     * @example
     * // formula -> CONCAT('hello', 'world', 'xpto')
     *
     * convertVariadicToArray(formula)
     *
     * // result -> CONCAT('hello', ['world', 'xpto'])
     *
     * @param node - The formula node to convert variadic arguments.
     */
    convertVariadicToArray(node) {
        const formulaDef = sheet_db_1.FormulaLibrary[node.abstractText];
        const variadicIndex = formulaDef?.args_def.findIndex(args => args.variadic) ?? -1;
        if (variadicIndex === -1)
            return;
        let children = [...node.children];
        const variadicChildren = children.slice(variadicIndex);
        children.splice(variadicIndex, variadicChildren.length);
        const nodeWithVariadic = new array_node_1.ArrayNode(variadicChildren, node);
        children.push(nodeWithVariadic);
        node.children = children;
        this.addConcreteNodesToFormula(node);
    }
    addQuotationMarks(value) {
        return `'${value}'`;
    }
}
exports.BqVisitor = BqVisitor;
